/**
 * 编辑器 Context 和 Hooks
 * 提供全局状态管理和双向绑定能力
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
} from 'react';
import type { SchemaInput, EditorState, EditorAction } from '../core/types';
import { generateScopedCSS } from '../core/cssGenerator';
import { immutableSet, getByPath } from '../utils/immutable';

// CSS 生成时使用的最大级别（始终生成 0-8 级的 CSS）
const CSS_MAX_LEVEL = 8;

// localStorage key
const STORAGE_KEY = 'word-numbering-schema';

// ============ Context ============

interface EditorContextValue {
  state: EditorState;
  dispatch: React.Dispatch<EditorAction>;
}

const EditorContext = createContext<EditorContextValue | null>(null);

// ============ Reducer ============

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'SET_SCHEMA_VALUE': {
      const newSchema = immutableSet(state.schema, action.path, action.value);
      const newCSS = generateScopedCSS(newSchema, state.selectedAbstractNumId, CSS_MAX_LEVEL).css;
      return {
        ...state,
        schema: newSchema,
        generatedCSS: newCSS,
        isDirty: true,
      };
    }

    case 'BATCH_UPDATE': {
      let newSchema = state.schema;
      for (const update of action.updates) {
        newSchema = immutableSet(newSchema, update.path, update.value);
      }
      const newCSS = generateScopedCSS(newSchema, state.selectedAbstractNumId, CSS_MAX_LEVEL).css;
      return {
        ...state,
        schema: newSchema,
        generatedCSS: newCSS,
        isDirty: true,
      };
    }

    case 'SELECT_ABSTRACT_NUM': {
      const newCSS = generateScopedCSS(state.schema, action.id, CSS_MAX_LEVEL).css;
      return {
        ...state,
        selectedAbstractNumId: action.id,
        activeLevel: 0,
        generatedCSS: newCSS,
      };
    }

    case 'SELECT_LEVEL':
      // 允许选择 0-8 的任意级别
      return {
        ...state,
        activeLevel: Math.min(Math.max(0, action.level), 8),
      };

    case 'RESET_SCHEMA': {
      const newCSS = generateScopedCSS(action.schema, state.selectedAbstractNumId, CSS_MAX_LEVEL).css;
      return {
        ...state,
        schema: action.schema,
        generatedCSS: newCSS,
        isDirty: false,
      };
    }

    case 'SET_EDITABLE_MAX_LEVEL':
      return {
        ...state,
        editableMaxLevels: {
          ...state.editableMaxLevels,
          [action.abstractNumId]: action.maxLevel,
        },
      };

    default:
      return state;
  }
}

// ============ Provider ============

interface EditorProviderProps {
  children: ReactNode;
  initialSchema: SchemaInput;
  initialAbstractNumId?: number;
}

export function EditorProvider({
  children,
  initialSchema,
  initialAbstractNumId = 0,
}: EditorProviderProps) {
  // 校验请求的 AbstractNum 是否存在，不存在则回退到第一个可用的
  const safeAbstractNumId = useMemo(() => {
    const key = `AbstractNum_${initialAbstractNumId}`;
    if (initialSchema.numberingSchemas[key]) {
      return initialAbstractNumId;
    }
    const firstKey = Object.keys(initialSchema.numberingSchemas)[0];
    const match = firstKey?.match(/^AbstractNum_(\d+)$/);
    return match ? Number(match[1]) : 0;
  }, [initialSchema, initialAbstractNumId]);

  const initialCSS = useMemo(
    () => generateScopedCSS(initialSchema, safeAbstractNumId, CSS_MAX_LEVEL).css,
    [initialSchema, safeAbstractNumId]
  );

  const [state, dispatch] = useReducer(editorReducer, {
    schema: initialSchema,
    selectedAbstractNumId: safeAbstractNumId,
    activeLevel: 0,
    generatedCSS: initialCSS,
    isDirty: false,
    editableMaxLevels: {}, // 初始为空，默认值在使用时处理
  });

  // 自动保存到localStorage
  useEffect(() => {
    if (state.isDirty) {
      const timer = setTimeout(() => {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(state.schema));
          console.log('Schema auto-saved to localStorage');
        } catch (error) {
          console.error('Failed to save schema to localStorage:', error);
        }
      }, 1000); // 1秒debounce

      return () => clearTimeout(timer);
    }
  }, [state.schema, state.isDirty]);

  const contextValue = useMemo(
    () => ({ state, dispatch }),
    [state, dispatch]
  );

  return (
    <EditorContext.Provider value={contextValue}>
      {children}
    </EditorContext.Provider>
  );
}

// ============ Hooks ============

/**
 * 获取编辑器 Context
 */
export function useEditorContext(): EditorContextValue {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditorContext must be used within EditorProvider');
  }
  return context;
}

/**
 * 获取当前 Schema
 */
export function useSchema(): SchemaInput {
  const { state } = useEditorContext();
  return state.schema;
}

/**
 * 获取生成的 CSS
 */
export function useGeneratedCSS(): string {
  const { state } = useEditorContext();
  return state.generatedCSS;
}

/**
 * 获取当前选中的 AbstractNum ID
 */
export function useSelectedAbstractNumId(): [number, (id: number) => void] {
  const { state, dispatch } = useEditorContext();

  const setId = useCallback(
    (id: number) => dispatch({ type: 'SELECT_ABSTRACT_NUM', id }),
    [dispatch]
  );

  return [state.selectedAbstractNumId, setId];
}

/**
 * 获取当前激活的 Level
 */
export function useActiveLevel(): [number, (level: number) => void] {
  const { state, dispatch } = useEditorContext();

  const setLevel = useCallback(
    (level: number) => dispatch({ type: 'SELECT_LEVEL', level }),
    [dispatch]
  );

  return [state.activeLevel, setLevel];
}

/**
 * 创建 Schema 绑定
 * @param path - Schema 路径
 * @param transform - 可选的值转换器
 */
export function useSchemaBinding<T, S = T>(
  path: string[],
  transform?: {
    toUI?: (schemaValue: S) => T;
    toSchema?: (uiValue: T) => S;
  }
): [T, (value: T) => void] {
  const { state, dispatch } = useEditorContext();

  const value = useMemo(() => {
    const raw = getByPath<S>(state.schema, path);
    return transform?.toUI ? transform.toUI(raw as S) : (raw as unknown as T);
  }, [state.schema, path, transform]);

  const setValue = useCallback(
    (newValue: T) => {
      const schemaValue = transform?.toSchema
        ? transform.toSchema(newValue)
        : newValue;
      dispatch({
        type: 'SET_SCHEMA_VALUE',
        path,
        value: schemaValue,
      });
    },
    [path, transform, dispatch]
  );

  return [value, setValue];
}

/**
 * 创建 Level 属性绑定
 */
export function useLevelBinding<T, S = T>(
  property: string[],
  transform?: {
    toUI?: (schemaValue: S) => T;
    toSchema?: (uiValue: T) => S;
  }
): [T, (value: T) => void] {
  const { state } = useEditorContext();
  const { selectedAbstractNumId, activeLevel } = state;

  const path = useMemo(
    () => [
      'numberingSchemas',
      `AbstractNum_${selectedAbstractNumId}`,
      'levels',
      String(activeLevel),
      ...property,
    ],
    [selectedAbstractNumId, activeLevel, property]
  );

  return useSchemaBinding<T, S>(path, transform);
}

/**
 * 创建样式属性绑定
 */
export function useStyleBinding<T, S = T>(
  styleId: string,
  property: string[],
  transform?: {
    toUI?: (schemaValue: S) => T;
    toSchema?: (uiValue: T) => S;
  }
): [T, (value: T) => void] {
  const path = useMemo(
    () => ['paragraphStyles', styleId, ...property],
    [styleId, property]
  );

  return useSchemaBinding<T, S>(path, transform);
}

/**
 * 获取当前 Level 关联的样式 ID
 */
export function useCurrentLevelStyleId(): string | undefined {
  const { state } = useEditorContext();
  const { schema, selectedAbstractNumId, activeLevel } = state;

  return useMemo(() => {
    const abstractNum = schema.numberingSchemas[`AbstractNum_${selectedAbstractNumId}`];
    return abstractNum?.levels[activeLevel]?.style;
  }, [schema, selectedAbstractNumId, activeLevel]);
}

/**
 * 获取当前 Level 的完整定义
 */
export function useCurrentLevel() {
  const { state } = useEditorContext();
  const { schema, selectedAbstractNumId, activeLevel } = state;

  return useMemo(() => {
    const abstractNum = schema.numberingSchemas[`AbstractNum_${selectedAbstractNumId}`];
    return abstractNum?.levels[activeLevel];
  }, [schema, selectedAbstractNumId, activeLevel]);
}

/**
 * 批量更新 hook
 */
export function useBatchUpdate() {
  const { dispatch } = useEditorContext();

  return useCallback(
    (updates: Array<{ path: string[]; value: unknown }>) => {
      dispatch({ type: 'BATCH_UPDATE', updates });
    },
    [dispatch]
  );
}

/**
 * 重置 Schema
 */
export function useResetSchema() {
  const { dispatch } = useEditorContext();

  return useCallback(
    (schema: SchemaInput) => {
      dispatch({ type: 'RESET_SCHEMA', schema });
    },
    [dispatch]
  );
}

/**
 * 检查是否有未保存的更改
 */
export function useIsDirty(): boolean {
  const { state } = useEditorContext();
  return state.isDirty;
}

/**
 * 获取当前编辑的最大级别
 */
export function useEditableMaxLevel(): [number, (level: number) => void] {
  const { state, dispatch } = useEditorContext();
  const maxLevel = state.editableMaxLevels[state.selectedAbstractNumId] ?? 4; // 默认为4

  const setMaxLevel = useCallback(
    (level: number) => {
      dispatch({
        type: 'SET_EDITABLE_MAX_LEVEL',
        abstractNumId: state.selectedAbstractNumId,
        maxLevel: level,
      });
    },
    [dispatch, state.selectedAbstractNumId]
  );

  return [maxLevel, setMaxLevel];
}
